import * as Sentry from "@sentry/nuxt";
import { addressFormRequiredConstraints } from "@shared/validation/address";
import { useQuery } from "@tanstack/vue-query";
import type {
  BidDetailsProduct,
  EventDetails,
  MaxBid,
  Price,
  ProductDetails,
} from "mws-ts-v2-sdk";

import BaseCallToActionToast from "~/components/base/feedback/BaseCallToActionToast.vue";
import { queries } from "~/queries";
import { useCurrentCustomerQuery } from "~/queries/finance/customers";
import { BidAttemptType, BidErrorType, BidModalEnum } from "~/types/Catalog";
import { getPriceInCurrency } from "~/utils/priceUtils";

export const useBidding = (
  data: {
    product: MaybeRefOrGetter<ProductDetails | BidDetailsProduct>;
    event?: MaybeRefOrGetter<EventDetails>;
    activeMaxBid?: MaybeRefOrGetter<MaxBid | null>;
    bidTypes?: BidAttemptType[];
  },
  onUpdatePendingState?: (pendingState: boolean) => void,
) => {
  const i18n = useI18n();

  const { bidVerified, bidBlocked, bidLimitInActiveCurrency } = useBidder();

  const { activeCurrency } = useActiveCurrency();

  const bidAttemptType = ref<BidAttemptType | undefined>();
  const bidModalType = ref<BidModalEnum | undefined>(undefined);
  const onCloseBidModal = () => (bidModalType.value = undefined);

  const { openOnboardingDialog } = useOnboardingDialog();
  const { data: paymentTokens } = useQuery(queries.tokens.list());

  const {
    quickBid,
    bidAmount,
    maxBidClicked,
    raiseMaxBidClicked,
    preAuthCreditCardSelected,
  } = useAnalytics();

  const getRoundedValueInCents = (amountInCents: number) =>
    Math.ceil(amountInCents) * 100;

  const getAmountFromChoice = (choice: Price[]) =>
    getRoundedValueInCents(
      getPriceInCurrency(choice, activeCurrency.value.isoCurrencyCode),
    );

  const bidAmountInCents = ref<number | undefined>();
  const fullBidAmount = computed(() => {
    return bidAmountInCents.value !== undefined
      ? bidAmountInCents.value / 100
      : 0;
  });

  const userActiveCards = computed(() => {
    const tokens = paymentTokens.value?.results || [];

    return tokens.filter((token) => token.isTokenized);
  });

  const minimumBid = computed(() => {
    const minimumBidPrices =
      toValue(data.product).availableQuickBidPrices?.[0] ?? [];

    const activeMaxBidValue = toValue(data.activeMaxBid);
    if (activeMaxBidValue) {
      const minimumMaxBidPrices =
        activeMaxBidValue.prices?.map((price) => ({
          ...price,
          price: price.price + 1,
        })) ?? [];

      const minimumBidPriceInEuro = getPriceInCurrency(minimumBidPrices, "EUR");
      const minimumMaxBidPriceInEuro = getPriceInCurrency(
        minimumMaxBidPrices,
        "EUR",
      );

      // Either set the max bid + 1 as the minimum bid
      // or set the minimum bid price from the quick bid.
      // This can happen if the current bid is close to the user's max bid.
      if (minimumMaxBidPriceInEuro > minimumBidPriceInEuro) {
        return minimumMaxBidPrices;
      }
    }

    return minimumBidPrices;
  });

  const minimumBidValueInActiveCurrency = computed(() =>
    Math.ceil(
      getPriceInCurrency(
        minimumBid.value,
        activeCurrency.value.isoCurrencyCode,
      ),
    ),
  );

  const submitBidPending = ref(false);
  const onSetPendingState = (pending: boolean) => {
    submitBidPending.value = pending;
    onUpdatePendingState?.(pending);
  };

  const isBiddingDisabled = computed(() => {
    return submitBidPending.value || toValue(data.product).finished;
  });

  const { data: customer } = useCurrentCustomerQuery();

  const customerShippingAddress = computed(() =>
    customer.value?.addresses.find((a) => a.isShippingAddress),
  );

  const { ensureBidCostsQuery } = useBidCosts({
    product: () => toValue(data.product),
    bidAmount: () => fullBidAmount.value,
  });

  const trackBidClicked = (bidAttemptType: BidAttemptType) => {
    const productValue = toValue(data.product);
    const eventValue = toValue(data.event);
    const activeMaxBidValue = toValue(data.activeMaxBid);

    const bidIncrement =
      fullBidAmount.value -
      getPriceInCurrency(
        productValue.prices,
        activeCurrency.value.isoCurrencyCode,
      );

    if (bidAttemptType === BidAttemptType.Quickbid) {
      return quickBid(
        productValue,
        eventValue,
        bidIncrement,
        userActiveCards.value.length,
        bidVerified.value,
      );
    }

    if (bidAttemptType === BidAttemptType.Bid) {
      return bidAmount(
        productValue,
        eventValue,
        bidIncrement,
        userActiveCards.value.length,
        bidVerified.value,
      );
    }

    if (activeMaxBidValue) {
      return raiseMaxBidClicked(
        productValue,
        eventValue,
        bidIncrement,
        fullBidAmount.value,
        getPriceInCurrency(
          activeMaxBidValue.prices ?? [],
          activeCurrency.value.isoCurrencyCode,
        ),
      );
    } else {
      return maxBidClicked(
        productValue,
        eventValue,
        bidIncrement,
        fullBidAmount.value,
        userActiveCards.value.length,
        bidVerified.value,
      );
    }
  };

  const initializeBiddingAttempt = async (options: {
    bidAttemptType: BidAttemptType;
  }) => {
    bidAttemptType.value = options.bidAttemptType;

    if (!bidAmountInCents.value) {
      throw new Error("No bid amount set");
    }

    trackBidClicked(options.bidAttemptType);

    // if user is bid blocked account status, show BidLimitWarningModal
    if (bidBlocked.value) {
      return (bidModalType.value = BidModalEnum.LimitWarning);
    }

    if (!bidVerified.value || !bidLimitInActiveCurrency.value) {
      openOnboardingDialog({
        type: OnboardingEntryType.Bid,
        data: {
          bidAmount: fullBidAmount.value,
        },
      });

      return;
    }

    const bidExceedsLimit =
      bidLimitInActiveCurrency.value < fullBidAmount.value;

    if (bidExceedsLimit) {
      bidModalType.value = BidModalEnum.LimitWarning;

      return;
    }

    if (
      addressFormRequiredConstraints.state({
        countryCode: customerShippingAddress.value?.countryCode ?? "",
      }) &&
      !customerShippingAddress.value?.state
    ) {
      return (bidModalType.value = BidModalEnum.AddressRequired);
    }

    const bidCosts = await ensureBidCostsQuery();
    if (bidCosts?.preAuthorisationResponse?.isRequired) {
      return (bidModalType.value = BidModalEnum.ReservationRequired);
    }

    bidModalType.value = BidModalEnum.Confirm;
  };

  const onMaxBidProductBidPlaced = (data: {
    isOutbid: boolean;
    isExtended: boolean;
  }) => {
    if (data.isOutbid) {
      bidModalType.value = data.isExtended
        ? BidModalEnum.OutbidExtended
        : BidModalEnum.Outbid;
    } else {
      bidModalType.value = data.isExtended
        ? BidModalEnum.PlacedExtended
        : BidModalEnum.Placed;
    }
  };

  const { mutateAsync: postBidMutateAsync } = usePostBidMutation(
    {
      product: data.product,
      event: data.event,
      activeMaxBid: data.activeMaxBid,
    },
    {
      onMaxBidProductBidPlaced,
      onNormalProductBidPlaced: onCloseBidModal,
      onMaxBidRaised: onCloseBidModal,
    },
  );

  // Payment tokenisation logic
  const route = useRoute();
  const router = useRouter();
  const redirectDataString = computed(() =>
    transformRouteParams(route.query.redirectData, "string"),
  );

  watch(
    redirectDataString,
    (newValue) => {
      if (!newValue) return;

      const redirectData: { bidAmount: number; bidType: BidAttemptType } =
        JSON.parse(newValue);

      if (!redirectData || !data.bidTypes?.includes(redirectData.bidType))
        return;

      bidModalType.value = BidModalEnum.ReservationRequired;
      bidAttemptType.value = redirectData.bidType;
      bidAmountInCents.value = redirectData.bidAmount * 100;
      router.replace({
        ...route,
        query: {
          modal: undefined,
        },
      });
    },
    { immediate: true },
  );

  const paymentTokenId = ref();
  const onPaymentTokenSelected = (tokenId?: string) => {
    if (!tokenId) return;

    paymentTokenId.value = tokenId;
    bidModalType.value = BidModalEnum.Confirm;
    preAuthCreditCardSelected({
      number_active_cards: userActiveCards.value.length,
    });
  };

  const bidError = useBidErrorState();
  const submitBid = async (marketingOptIn?: boolean, tokenId?: string) => {
    bidError.value = null;

    try {
      if (!bidAmountInCents.value || !fullBidAmount.value) {
        throw new Error(
          "Trying to submit a bid but the bidAmount is undefined",
        );
      }

      if (
        !customerShippingAddress.value?.countryCode ||
        !bidAttemptType.value
      ) {
        throw new Error("Trying to submit a bid but some values are missing");
      }
    } catch (error: any) {
      // Handle errors while still sending them to sentry
      bidError.value = {
        errorType: BidErrorType.MissingValues,
        message: error.message,
        errorHandler: () => (bidError.value = null),
      };

      return Sentry.captureException(error);
    }

    try {
      onSetPendingState(true);

      await postBidMutateAsync({
        currencyCode: activeCurrency.value.isoCurrencyCode,
        partnerMarketingConsent: marketingOptIn ?? false,
        customerCountryCode: customerShippingAddress.value.countryCode,
        bidAttemptType: bidAttemptType.value,
        amount: {
          full: fullBidAmount.value,
          inCents: bidAmountInCents.value,
        },
        paymentTokenId: tokenId ?? null,
      });

      onSetPendingState(false);
      bidAmountInCents.value = undefined;
      bidAttemptType.value = undefined;
      paymentTokenId.value = undefined;
    } catch {
      // handled in the mutation definition
      onSetPendingState(false);
    }
  };

  onUnmounted(() => {
    bidError.value = null;
  });

  return {
    /**
     * Initializes a bidding attempt. `bidAmountInCents` must be set before calling this function. Checks if user state is suitable for making a bid and if so, opens a confirmation modal.
     */
    initializeBiddingAttempt,
    bidAttemptType,

    /**
     * Clears the previous bid errors and submits a bid with the bidAmount in the component/composable state. Clears the `bidAmountInCents` after submitting the bid.
     */
    submitBid,

    /**
     * Loading state for the bid being sent to the backend
     */
    isSubmitBidPending: submitBidPending,

    /**
     * bidAmount that will be used when submitBid is called. This **must** be set before initalizing a bid.
     */
    bidAmountInCents,
    /**
     * Full bid amount.
     */
    fullBidAmount,
    /**
     * The first quick bid option of the product
     */
    minimumBid,
    /**
     * The value of the first quick bid option in the selected currency
     */
    minimumBidValueInActiveCurrency,

    // helper functions
    getAmountFromChoice,
    getRoundedValueInCents,

    /**
     * Bidding is considered disabled when there is already a bidding pending or the product is finished
     */
    isBiddingDisabled,

    bidModalType,
    closeBidModal: onCloseBidModal,

    paymentTokenId,
    paymentTokenSelected: onPaymentTokenSelected,

    activeCurrency,
    locale: i18n.locale,
  };
};

/**
 * Composable to handle the `bidError` set by the catalog module.
 *
 * Important note: if this composable is used multiple times in a page, it will trigger the modal multiple times
 * @todo refactor the product page to have only one instance of the bid error handling modals
 *
 * @param bidLimitWarningModal ref for the modal that should be displayed in the case of a bidLimitExceeded error
 */
export const useBidError = () => {
  const platformStore = usePlatformStore();
  const toast = useToast();
  const i18n = useI18n();
  const { openOnboardingDialog } = useOnboardingDialog();
  const bidError = useBidErrorState();

  watch(bidError, (newBidError) => {
    if (!newBidError) return;

    if (newBidError.errorType === BidErrorType.NoBidUserOrNoExchangeRates) {
      toast.error(
        i18n.t("errors.nonSpecifiedBidError", {
          errorCode: ` (${newBidError.errorCode})`,
        }),
      );
    }

    if (newBidError.errorType === BidErrorType.BidLimitExceeded) {
      openOnboardingDialog({
        type: OnboardingEntryType.Bid,
        data: {
          bidAmount: newBidError.bidAmount ?? null,
        },
      });
    }

    if (newBidError.errorType === BidErrorType.MissingValues) {
      toast.error({
        component: BaseCallToActionToast,
        props: {
          htmlMessage: i18n.t("errors.missingValuesBidError"),
          callToAction: {
            text: i18n.t("common.contactSupport"),
            path: platformStore.getCustomerSupportLink,
          },
        },
      });
    }

    newBidError.errorHandler?.();
  });

  return {
    bidError,
  };
};
